/**
 * Report spec type mirror — keeps the frontend's ReportSpec shape in sync
 * with the backend Zod schema at apps/backend/src/reports/report-spec.js.
 *
 * Kept as a hand-written type because the frontend does not run Zod at
 * runtime — validation happens server-side on save. Fields follow snake_case
 * from the backend contract.
 */

export type MetricName =
  | 'revenue_cents'
  | 'net_revenue_cents'
  | 'scan_count'
  | 'paid_scan_count'
  | 'unique_guests'
  | 'orders_count'
  | 'refunds_cents'
  | 'avg_wait_minutes'
  | 'plan_mix'
  | 'kiosk_throughput'
  | 'top_attractions'
  | 'top_plans'
  | 'daily_revenue'
  | 'peak_hours'
  | 'breakage_cents'
  | 'repeat_play_rate'

export const METRIC_NAMES: MetricName[] = [
  'revenue_cents',
  'net_revenue_cents',
  'scan_count',
  'paid_scan_count',
  'unique_guests',
  'orders_count',
  'refunds_cents',
  'avg_wait_minutes',
  'plan_mix',
  'kiosk_throughput',
  'top_attractions',
  'top_plans',
  'daily_revenue',
  'peak_hours',
  'breakage_cents',
  'repeat_play_rate',
]

export interface LayoutCoord {
  x: number
  y: number
  w: number
  h: number
}

interface WidgetBase {
  id: string
  title: string
  layout: LayoutCoord
}

export interface KpiWidget extends WidgetBase {
  type: 'kpi'
  metric: MetricName
  compare_to?: { relative: 'prev_period' | 'prev_year' }
}

export interface BarWidget extends WidgetBase {
  type: 'bar'
  metric: MetricName
  group_by: 'attraction' | 'plan' | 'day' | 'hour' | 'channel'
  limit: number
}

export interface LineWidget extends WidgetBase {
  type: 'line'
  metric: MetricName
  grain: 'hour' | 'day'
  overlay: MetricName[]
}

export interface TableWidget extends WidgetBase {
  type: 'table'
  metric: MetricName
  columns: string[]
  group_by?: 'attraction' | 'plan' | 'day' | 'hour' | 'channel' | 'operator'
  limit: number
}

export interface ComparisonWidget extends WidgetBase {
  type: 'comparison'
  metrics: MetricName[]
  left_event_id?: number
  right_event_id?: number
}

export type WidgetSpec =
  | KpiWidget
  | BarWidget
  | LineWidget
  | TableWidget
  | ComparisonWidget

export type RelativeRange = 'event_days' | 'last_7d' | 'last_30d' | 'ytd' | 'all_time'

export type DateRange =
  | { relative: RelativeRange }
  | { start: string; end: string }

export interface EventScope {
  event_ids: number[]
  include_all: boolean
}

export interface ReportSpec {
  schema_version: 1
  name: string
  description?: string
  event_scope: EventScope
  date_range: DateRange
  widgets: WidgetSpec[]
}

export interface ReportSummary {
  id: number
  slug: string
  name: string
  description?: string | null
  kind: 'report'
  created_at: string
  updated_at: string
}

export interface ReportFull extends ReportSummary {
  spec: ReportSpec
}

export interface WidgetExecutionResult {
  widgetId: string
  type: WidgetSpec['type']
  data: unknown
  error?: string
  generated_at: string
}

export interface ReportExecutionResult {
  report_name: string
  generated_at: string
  widgets: WidgetExecutionResult[]
}
